/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useState} from 'react';
import {useParams} from 'react-router';
import {navigate, ResponseData, showDialog} from '@dvrd/dvr-controls';
import {ResetPasswordErrors} from '../../../../util/interfaces';
import {updatePassword} from '../../../../util/requestSender';
import {createErrorMessage} from '../../../../util/utils';
import ResetPassword from './resetPassword';
import {LOGIN_ROUTE} from '../../../../util/constants';
import {useTranslation} from 'react-i18next';

export default function ResetPasswordController() {
    const {t} = useTranslation();
    const params = useParams<{code: string}>();
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<ResetPasswordErrors>({confirmPassword: null, password: null});
    if (!params.code) {
        navigate(LOGIN_ROUTE);
        return null;
    }

    function onChange(key: 'pass' | 'confirm') {
        return function (value: string) {
            if (key === 'pass') setPassword(value);
            else setConfirm(value);
        };
    }

    function onResetError(key: keyof ResetPasswordErrors) {
        return function () {
            setErrors(Object.assign({}, errors, {[key]: null}));
        };
    }

    function onSubmit() {
        if (!validateValues()) return;
        setLoading(true);
        updatePassword({
            data: {password: null, new_password: password, activation_key: params.code!},
            callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    showDialog(
                        t(
                            'auth.resetPassword.successMessage',
                            'Het wachtwoord van je account is aangepast, je kunt nu met je nieuwe wachtwoord inloggen.'
                        ),
                        t('auth.resetPassword.successTitle', 'Wachtwoord gewijzigd')
                    );
                    navigate('/inloggen');
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t('auth.resetPassword.failedMessage', 'Het wijzigen van je wachtwoord is niet gelukt.')
                        ),
                        t('auth.resetPassword.failedTitle', 'Wijzigen mislukt')
                    );
            },
            // A blocked/non-2xx request (e.g. a Cloudflare 403 after the app sat
            // idle) would otherwise never resolve the spinner. Reset loading and
            // show a retryable error instead of hanging.
            errorCallback: () => {
                setLoading(false);
                showDialog(
                    createErrorMessage(
                        t(
                            'auth.resetPassword.connectionError',
                            'Verbinding mislukt — controleer je internet en probeer opnieuw.'
                        )
                    ),
                    t('auth.resetPassword.failedTitle', 'Wijzigen mislukt')
                );
            }
        });
    }

    function validateValues() {
        let valid: boolean = true;
        const _errors = Object.assign({}, errors);
        if (!password) {
            valid = false;
            _errors.password = t('auth.resetPassword.passwordRequired', 'Kies een nieuw wachtwoord');
        } else if (!confirm) {
            valid = false;
            _errors.confirmPassword = t('auth.resetPassword.confirmRequired', 'Herhaal het wachtwoord');
        } else if (confirm !== password) {
            valid = false;
            _errors.confirmPassword = t('auth.resetPassword.passwordsMismatch', 'De wachtwoorden komen niet overeen');
        }
        if (!valid) setErrors(_errors);
        return valid;
    }

    return (
        <ResetPassword
            onChange={onChange}
            onSubmit={onSubmit}
            onResetError={onResetError}
            password={password}
            confirmPassword={confirm}
            loading={loading}
            errors={errors}
        />
    );
}
